// LinkedIn API integration for posting articles
// Note: This requires LinkedIn API credentials and proper OAuth setup

const LINKEDIN_API_BASE = 'https://api.linkedin.com/v2';

// LinkedIn OAuth configuration
const LINKEDIN_CONFIG = {
  clientId: process.env.REACT_APP_LINKEDIN_CLIENT_ID,
  redirectUri: process.env.REACT_APP_LINKEDIN_REDIRECT_URI,
  scope: 'w_member_social,r_liteprofile,r_emailaddress'
};

// Generate LinkedIn OAuth URL
export const getLinkedInAuthUrl = () => {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: LINKEDIN_CONFIG.clientId,
    redirect_uri: LINKEDIN_CONFIG.redirectUri,
    scope: LINKEDIN_CONFIG.scope,
    state: Math.random().toString(36).substring(7) // Random state for security
  });
  
  return `https://www.linkedin.com/oauth/v2/authorization?${params.toString()}`;
};

// Exchange authorization code for access token
export const exchangeCodeForToken = async (code) => {
  try {
    const response = await fetch('https://www.linkedin.com/oauth/v2/accessToken', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: LINKEDIN_CONFIG.redirectUri,
        client_id: LINKEDIN_CONFIG.clientId,
        client_secret: process.env.REACT_APP_LINKEDIN_CLIENT_SECRET,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange code for token');
    }

    const data = await response.json();
    return data.access_token;
  } catch (error) {
    console.error('Error exchanging code for token:', error);
    throw error;
  }
};

// Get LinkedIn profile information
export const getLinkedInProfile = async (accessToken) => {
  try {
    const response = await fetch(`${LINKEDIN_API_BASE}/me`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get LinkedIn profile');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting LinkedIn profile:', error);
    throw error;
  }
};

// Post article to LinkedIn
export const postToLinkedIn = async (accessToken, articleData) => {
  try {
    const { title, excerpt, articleUrl, imageUrl } = articleData;
    
    // First, get the user's profile ID
    const profile = await getLinkedInProfile(accessToken);
    const authorUrn = `urn:li:person:${profile.id}`;

    // Prepare the post content
    const postData = {
      author: authorUrn,
      lifecycleState: 'PUBLISHED',
      specificContent: {
        'com.linkedin.ugc.ShareContent': {
          shareCommentary: {
            text: `${title}\n\n${excerpt}\n\nRead more: ${articleUrl}\n\n#APN #Finance #Professionals #Networking`
          },
          shareMediaCategory: 'ARTICLE',
          media: [
            {
              status: 'READY',
              description: {
                text: excerpt
              },
              media: articleUrl,
              title: {
                text: title
              },
              ...(imageUrl && {
                thumbnails: [
                  {
                    url: imageUrl
                  }
                ]
              })
            }
          ]
        }
      },
      visibility: {
        'com.linkedin.ugc.MemberNetworkVisibility': 'PUBLIC'
      }
    };

    const response = await fetch(`${LINKEDIN_API_BASE}/ugcPosts`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'X-Restli-Protocol-Version': '2.0.0'
      },
      body: JSON.stringify(postData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`LinkedIn API error: ${errorData.message || 'Unknown error'}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error posting to LinkedIn:', error);
    throw error;
  }
};

// Simplified LinkedIn sharing (opens LinkedIn share dialog)
export const shareOnLinkedIn = (articleData) => {
  const { title, excerpt, articleUrl } = articleData;
  const text = `${title}\n\n${excerpt}\n\n#APN #Finance #Professionals #Networking`;
  
  const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(excerpt)}`;
  
  // Open LinkedIn share dialog in new window
  const popup = window.open(
    shareUrl,
    'linkedin-share',
    'width=600,height=400,scrollbars=yes,resizable=yes'
  );
  
  return popup;
};

// Company page posting (requires company page admin access)
export const postToLinkedInCompanyPage = async (accessToken, companyId, articleData) => {
  try {
    const { title, excerpt, articleUrl, imageUrl } = articleData;
    
    const postData = {
      author: `urn:li:organization:${companyId}`,
      lifecycleState: 'PUBLISHED',
      specificContent: {
        'com.linkedin.ugc.ShareContent': {
          shareCommentary: {
            text: `${title}\n\n${excerpt}\n\nRead more: ${articleUrl}\n\n#APN #Finance #Professionals #Networking #AcceleratedProfessionalsNetwork`
          },
          shareMediaCategory: 'ARTICLE',
          media: [
            {
              status: 'READY',
              description: {
                text: excerpt
              },
              media: articleUrl,
              title: {
                text: title
              },
              ...(imageUrl && {
                thumbnails: [
                  {
                    url: imageUrl
                  }
                ]
              })
            }
          ]
        }
      },
      visibility: {
        'com.linkedin.ugc.MemberNetworkVisibility': 'PUBLIC'
      }
    };

    const response = await fetch(`${LINKEDIN_API_BASE}/ugcPosts`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'X-Restli-Protocol-Version': '2.0.0'
      },
      body: JSON.stringify(postData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`LinkedIn API error: ${errorData.message || 'Unknown error'}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error posting to LinkedIn company page:', error);
    throw error;
  }
};

// Store LinkedIn access token securely
export const storeLinkedInToken = (token) => {
  // In production, this should be stored securely on the server
  localStorage.setItem('linkedin_access_token', token);
};

// Get stored LinkedIn access token
export const getStoredLinkedInToken = () => {
  return localStorage.getItem('linkedin_access_token');
};

// Remove stored LinkedIn access token
export const removeLinkedInToken = () => {
  localStorage.removeItem('linkedin_access_token');
};

// Check if LinkedIn is connected
export const isLinkedInConnected = () => {
  return !!getStoredLinkedInToken();
};

export default {
  getLinkedInAuthUrl,
  exchangeCodeForToken,
  getLinkedInProfile,
  postToLinkedIn,
  shareOnLinkedIn,
  postToLinkedInCompanyPage,
  storeLinkedInToken,
  getStoredLinkedInToken,
  removeLinkedInToken,
  isLinkedInConnected
};
